import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Lock, Eye, Trash2, Server } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Privacy Policy',
      subtitle: 'Your data belongs to you. Here\'s how we protect it.',
      lastUpdated: 'Last updated: March 2024',
      sections: [
        {
          icon: Lock,
          title: 'Data Security',
          points: [
            'Your password is encrypted using industry-standard hashing (never stored in plain text)',
            'All data transmission is encrypted via HTTPS',
            'Authentication uses secure JWT tokens',
            'Only you can access your personal assessments and reflections',
          ],
        },
        {
          icon: Eye,
          title: 'What We Collect',
          points: [
            'Account info: Email, name, and profile details you provide',
            'Assessment data: Your life domain scores and written reflections',
            'Check-in data: Morning and evening reflections',
            'Usage data: Basic app interactions to improve the experience',
          ],
        },
        {
          icon: Server,
          title: 'How We Use Your Data',
          points: [
            'To generate your personalized Sun Map and AI guidance',
            'To track your progress over time',
            'To provide personalized resource recommendations',
            'We NEVER sell your data to third parties',
          ],
        },
        {
          icon: Shield,
          title: 'AI & Third Parties',
          points: [
            'AI coaching uses OpenAI\'s API with business-tier privacy protections',
            'OpenAI does not use API data to train their models',
            'Voice transcription (Whisper) processes audio but does not store it',
            'No data is shared with advertisers or data brokers',
          ],
        },
        {
          icon: Trash2,
          title: 'Your Rights',
          points: [
            'You can delete all your data at any time from Settings',
            'Data deletion is permanent and cannot be undone',
            'You can export your Sun Map as an image',
            'Contact us anytime with privacy questions',
          ],
        },
      ],
      backToApp: 'Back to App',
    },
    tr: {
      title: 'Gizlilik Politikası',
      subtitle: 'Verileriniz size aittir. İşte onları nasıl koruyoruz.',
      lastUpdated: 'Son güncelleme: Mart 2024',
      sections: [
        {
          icon: Lock,
          title: 'Veri Güvenliği',
          points: [
            'Şifreniz endüstri standardı şifreleme ile korunur (asla düz metin olarak saklanmaz)',
            'Tüm veri aktarımı HTTPS ile şifrelenir',
            'Kimlik doğrulama güvenli JWT tokenları kullanır',
            'Kişisel değerlendirmelerinize ve yansımalarınıza yalnızca siz erişebilirsiniz',
          ],
        },
        {
          icon: Eye,
          title: 'Ne Topluyoruz',
          points: [
            'Hesap bilgileri: E-posta, isim ve sağladığınız profil detayları',
            'Değerlendirme verileri: Yaşam alanı puanlarınız ve yazılı yansımalarınız',
            'Kontrol verileri: Sabah ve akşam yansımalarınız',
            'Kullanım verileri: Deneyimi iyileştirmek için temel uygulama etkileşimleri',
          ],
        },
        {
          icon: Server,
          title: 'Verilerinizi Nasıl Kullanıyoruz',
          points: [
            'Kişiselleştirilmiş Güneş Haritanızı ve AI rehberliğinizi oluşturmak için',
            'Zaman içindeki ilerlemenizi takip etmek için',
            'Kişiselleştirilmiş kaynak önerileri sunmak için',
            'Verilerinizi ASLA üçüncü taraflara satmıyoruz',
          ],
        },
        {
          icon: Shield,
          title: 'AI ve Üçüncü Taraflar',
          points: [
            'AI koçluğu, kurumsal düzeyde gizlilik korumalı OpenAI API\'sini kullanır',
            'OpenAI, API verilerini modellerini eğitmek için kullanmaz',
            'Ses transkripsiyonu (Whisper) sesi işler ancak saklamaz',
            'Hiçbir veri reklamcılar veya veri simsarlarıyla paylaşılmaz',
          ],
        },
        {
          icon: Trash2,
          title: 'Haklarınız',
          points: [
            'Tüm verilerinizi istediğiniz zaman Ayarlar\'dan silebilirsiniz',
            'Veri silme kalıcıdır ve geri alınamaz',
            'Güneş Haritanızı görsel olarak dışa aktarabilirsiniz',
            'Gizlilik sorularınız için bize istediğiniz zaman ulaşın',
          ],
        },
      ],
      backToApp: 'Uygulamaya Dön',
    },
  };

  const c = content[language] || content.en;

  return (
    <div className="min-h-screen relative py-16 px-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="text-white/70 hover:text-white transition-colors duration-300 uppercase tracking-widest text-sm font-sans mb-8 flex items-center gap-2"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          {c.backToApp}
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Shield className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="font-serif font-light text-4xl text-[#F8F5F2] mb-4">
            {c.title}
          </h1>
          <p className="font-sans font-light text-[#D1D1D6] text-lg mb-2">
            {c.subtitle}
          </p>
          <p className="font-sans text-sm text-[#A1A1AA]">
            {c.lastUpdated}
          </p>
        </motion.div>

        <div className="space-y-8">
          {c.sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="glass-panel rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-[#D4AF37]/10">
                  <section.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h2 className="font-serif font-light text-xl text-[#F8F5F2]">
                  {section.title}
                </h2>
              </div>
              <ul className="space-y-3">
                {section.points.map((point, pointIndex) => (
                  <li
                    key={pointIndex}
                    className="font-sans font-light text-[#D1D1D6] flex items-start gap-3"
                  >
                    <span className="text-[#D4AF37] mt-1.5">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="font-sans text-sm text-[#A1A1AA]">
            {language === 'tr' 
              ? 'Sorularınız mı var? Bize ulaşın.' 
              : 'Have questions? Reach out to us.'}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
